import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  PersonOutline as PersonIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import useSWR from 'swr';
import { abha, type PatientProfile } from '@/api/clinical';
import { useAuth } from '@/auth/AuthContext';
import { activePatient, type ActivePatient } from '@/utils/activePatient';

/**
 * Patient picker — searchable list of registered patients for the
 * tenant. Clicking a row sets the {@link activePatient} session store,
 * which M2 / M3 forms read on mount.
 */
export function PatientPicker({
  open,
  onClose,
  onPicked,
}: {
  open: boolean;
  onClose: () => void;
  onPicked?: (p: ActivePatient) => void;
}) {
  const theme = useTheme();
  const { claims } = useAuth();
  const tenantId = claims?.tenantId ?? '';
  const [search, setSearch] = useState('');

  const { data, isLoading } = useSWR(
    tenantId && open ? ['picker-patients', tenantId, search] : null,
    () => abha.listPatients({ tenantId, page: 0, size: 50, user: search || undefined }),
  );

  const rawPicker = data?.data ?? [];
  const patients: PatientProfile[] = rawPicker.map((p) => ({
    ...p,
    abhaNumber: p.abhaNumber ?? p.abdmHealthId,
    abhaAddress: Array.isArray(p.abhaAddress) ? (p.abhaAddress as string[])[0] : p.abhaAddress,
    name: p.name ?? p.person?.name,
  }));

  const pick = (p: PatientProfile) => {
    const next: ActivePatient = {
      memberId: p.uhidNumber ?? p.memberNumber ?? p.id,
      abhaAddress: p.abhaAddress ?? '',
      abhaNumber: p.abhaNumber,
      name: p.name ?? p.person?.name ?? '',
      gender: (p.gender ?? 'unknown').toLowerCase(),
      birthDate: p.dob ?? (p.yearOfBirth ? `${p.yearOfBirth}-01-01` : undefined),
      mobile: p.mobile,
      address: typeof p.address === 'string' ? p.address : p.address?.line,
    };
    activePatient.set(next);
    onPicked?.(next);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        Pick a patient
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search by name, ABHA, mobile…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />

        {isLoading && <Typography variant="caption" color="text.secondary">Loading…</Typography>}

        {patients.length === 0 && !isLoading && (
          <Stack direction="column" alignItems="center" spacing={1.5} sx={{ py: 4 }}>
            <PersonIcon sx={{ fontSize: 36, color: theme.palette.text.tertiary }} />
            <Typography variant="body2" color="text.secondary">No patients yet.</Typography>
            <Typography variant="caption" color="text.tertiary">Onboard a patient via ABHA Creation first.</Typography>
          </Stack>
        )}

        <Stack spacing={0.5} sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {patients.map((p) => (
            <Box
              key={p.id}
              onClick={() => pick(p)}
              sx={{
                p: 1.5,
                borderRadius: theme.radius.sm + 'px',
                border: `1px solid ${theme.palette.border.subtle}`,
                cursor: 'pointer',
                transition: 'border-color 120ms, background-color 120ms',
                '&:hover': {
                  borderColor: theme.palette.brand.main,
                  bgcolor: theme.palette.brand.lighter,
                },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack spacing={0}>
                  <Typography variant="body2" fontWeight={500}>{p.name ?? p.person?.name ?? '—'}</Typography>
                  <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {p.abhaAddress ?? '—'} · UHID {p.uhidNumber ?? p.id}
                  </Typography>
                </Stack>
                <Stack alignItems="flex-end">
                  <Typography variant="caption" color="text.secondary">{p.gender ?? '—'}</Typography>
                  {p.mobile && <Typography variant="caption" color="text.tertiary">{p.mobile}</Typography>}
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
